import { useQuery } from '@tanstack/react-query';
import { Calendar, Clock, MapPin, ExternalLink, Copy, CheckCheck, Plus, Download, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService } from '../../services/bookingService';
import { Booking, ROOMS } from '../../types/booking';

const API_URL = (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL ?? 'http://localhost:8001';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending: { label: 'Upcoming', color: 'text-blue-500' },
  active: { label: 'Active', color: 'text-green-500' },
  completed: { label: 'Completed', color: 'text-gray-400' },
  cancelled: { label: 'Cancelled', color: 'text-red-400' },
};

function isWithin15Min(appointmentAt: string): boolean {
  const appt = new Date(appointmentAt).getTime();
  const now = Date.now();
  return appt - now <= 15 * 60 * 1000 && appt - now > -90 * 60 * 1000;
}

function roomName(roomId: string): string {
  return ROOMS.find((r) => r.id === roomId)?.name ?? roomId;
}

function CopyButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      title="Copy meeting link"
      className="text-gray-400 hover:text-blue-400 transition-colors"
    >
      {copied ? <CheckCheck className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function BookingCard({ booking, isPast = false }: { booking: Booking; isPast?: boolean }) {
  const ready = isWithin15Min(booking.appointment_at);
  const statusMeta = isPast && booking.status === 'pending'
    ? { label: 'Missed', color: 'text-gray-400' }
    : STATUS_LABEL[booking.status] ?? { label: booking.status, color: 'text-gray-400' };
  const active = booking.status === 'active' || (booking.status === 'pending' && ready);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-5 space-y-3 ${
      active ? 'border-blue-500/50' : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Calendar className="h-4 w-4 text-blue-400" />
            <span>{new Date(booking.appointment_at).toLocaleString('en-ZA', {
              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <MapPin className="h-4 w-4 text-purple-400" />
            <span>{roomName(booking.room_id)}</span>
          </div>
        </div>
        <span className={`text-xs font-medium ${statusMeta.color}`}>{statusMeta.label}</span>
      </div>

      {/* Meeting URL row */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
        <Clock className="h-3 w-3 flex-shrink-0" />
        <span className="truncate font-mono flex-1">{booking.meeting_url}</span>
        <CopyButton url={booking.meeting_url} />
      </div>

      {/* Enter Branch button */}
      {booking.status !== 'cancelled' && booking.status !== 'completed' && (
        <a
          href={booking.meeting_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium transition-colors ${
            active
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <ExternalLink className="h-4 w-4" />
          {active ? 'Enter Branch Now' : 'Enter Branch'}
        </a>
      )}

      {/* AC:8 (v8-7-1) — Download Minutes button for completed bookings */}
      {booking.status === 'completed' && (
        <a
          href={`${API_URL}/api/v1/bookings/${booking.id}/meeting/pdf`}
          download
          onClick={(e) => {
            // Attach JWT so the authenticated download works
            const token = localStorage.getItem('token') ?? '';
            if (!token) { e.preventDefault(); return; }
            // Use fetch+blob for authenticated download
            e.preventDefault();
            fetch(`${API_URL}/api/v1/bookings/${booking.id}/meeting/pdf`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then(async (res) => {
                if (!res.ok) return;
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `meeting-minutes-${booking.id.slice(0, 8)}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              })
              .catch(() => {/* non-critical */});
          }}
          className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors"
        >
          <Download className="h-4 w-4" />
          Download Minutes
        </a>
      )}
    </div>
  );
}

export default function AppointmentsDashboardPage() {
  const navigate = useNavigate();
  const [pastExpanded, setPastExpanded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['bookings'],
    queryFn: () => bookingService.list(),
  });

  const now = new Date();
  const upcoming = data?.items.filter(
    (b) => b.status === 'active' ||
           (b.status === 'pending' && new Date(b.appointment_at) > now)
  ) ?? [];
  const past = data?.items.filter(
    (b) => b.status === 'completed' || b.status === 'cancelled' ||
           (b.status === 'pending' && new Date(b.appointment_at) <= now)
  ) ?? [];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="h-7 w-7 text-blue-500" />
            Branch Appointments
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Your upcoming virtual branch visits
          </p>
        </div>
        <button
          onClick={() => navigate('/trading/appointments/book')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Book
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
          Failed to load appointments. Please try again.
        </div>
      )}

      {!isLoading && !error && upcoming.length === 0 && past.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Calendar className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto" />
          <p className="text-gray-500 dark:text-gray-400">No appointments yet.</p>
          <button
            onClick={() => navigate('/trading/appointments/book')}
            className="text-sm text-blue-500 hover:underline"
          >
            Book your first appointment
          </button>
        </div>
      )}

      {upcoming.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Upcoming
          </h2>
          {upcoming.map((b) => <BookingCard key={b.id} booking={b} />)}
        </section>
      )}

      {past.length > 0 && (
        <section className="space-y-3">
          <button
            onClick={() => setPastExpanded(v => !v)}
            className="flex items-center justify-between w-full text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <span>Past ({past.length})</span>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${pastExpanded ? 'rotate-180' : ''}`} />
          </button>
          {pastExpanded && past.map((b) => <BookingCard key={b.id} booking={b} isPast />)}
        </section>
      )}
    </div>
  );
}
